'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { tw } from '@tw';
import { PAGE_WIDTH } from '@/components/layout/PageFrame';
import { IntroVideo } from '@/components/media/IntroVideo';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { confirmSubscription, startSubscription } from '@/app/actions/subscriptions';
import { callAction } from '@/lib/call-action';
import { useOwnerMode } from '@/lib/stores/owner-store';
import { firstName as firstNameOf } from '@/lib/utils/displayName';
import { faceUrl } from '@/lib/utils/mux';
import { MIN_PLATFORM_FEE, PLATFORM_FEE, pct } from '@peach/types/payments/fees.js';
import type {
  CreatorSubscriptionsDto,
  SubscriptionPlanDto,
} from '@peach/types/subscriptions/subscription.dto.js';
import {
  PAUSE_WEEKS_OPTIONS,
  SUBSCRIPTION_PERIOD_WEEKS as WEEKS,
  planName,
} from '@peach/types/subscriptions/types.js';
import { PlansEditor } from '../subscriptions/PlansEditor';
import { QuizComposer } from './QuizComposer';
import { QuizPost } from './QuizPost';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';
import { SubscriptionCard } from '../subscriptions/SubscriptionCard';
import s from './sub.module.css';
import { EditableText } from '../EditableText';
import { updateProfile } from '@/app/actions/profile';
import { NAME_MAX } from '@peach/types/tutors/identity-limits.js';
import { useStickyTop } from './useStickyTop';

const EmbeddedCheckoutPanel = dynamic(() => import('@/components/payment/EmbeddedCheckoutPanel'), {
  ssr: false,
});

/** Stripe can take a moment to finish the subscription after the form says paid. */
const CONFIRM_TRIES = 10;
const CONFIRM_EVERY_MS = 1500;

const money = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
const totalOf = (plan: SubscriptionPlanDto) =>
  Math.round((plan.price + plan.platformFee) * 100) / 100;

interface SubscriptionPageProps {
  tutor: {
    username: string;
    name: string | null;
    picture: string | null;
    playbackId: string | null;
  };
  subscriptions: CreatorSubscriptionsDto;
  loggedIn: boolean;
  isOwner: boolean;
  serverNow: number;
  /** A client's passes and calls from before the switch: they still book them here. */
  earlier: ReactNode;
  /** Every quiz of the creator, newest first: free to play, the score after a sign-in, the
      right answers for members. `hidden` is marked in the owner's feed only. */
  quizzes: (SanitizedQuiz & { hidden?: boolean })[];
  fontClass: string;
}

/** The storefront of a creator on subscriptions: who they are, and their plans. */
export function SubscriptionPage({
  tutor,
  subscriptions: { plans, subscription },
  loggedIn,
  isOwner,
  serverNow,
  earlier,
  quizzes,
  fontClass,
}: SubscriptionPageProps) {
  const name = tutor.name || tutor.username;
  const first = firstNameOf({ name: tutor.name, username: tutor.username });
  const face = faceUrl({ picture: tutor.picture, playbackId: tutor.playbackId }, 40 * 2);
  const membership = plans.find((p) => p.callsPerWeek === 0) ?? null;
  const welcome = membership?.callMinutes ?? 0;
  // Plans come cheapest tier first: the door to the answers is the cheapest way in.
  const cheapest = plans[0] ?? null;
  const [pickedId, setPickedId] = useState(plans[0]?.id ?? null);
  const selected = plans.find((p) => p.id === pickedId) ?? plans[0] ?? null;
  const [sheet, setSheet] = useState<'billing' | 'checkout' | null>(null);

  const sideRef = useStickyTop<HTMLDivElement>();
  const router = useRouter();
  // The name is edited where it stands, as on every storefront (`OwnerIdentity`).
  const saveName = async (next: string) => {
    const result = await callAction(() => updateProfile({ name: next }, tutor.username));
    if (!result.success) return result.error;
    router.refresh();
    return null;
  };

  // The bar's "My page" pill is lit by this, as on every storefront.
  const setOwning = useOwnerMode((state) => state.setOwning);
  useEffect(() => {
    setOwning(isOwner);
    return () => setOwning(false);
  }, [isOwner, setOwning]);

  const rules = [
    'Billed every 4 weeks. Cancel any time.',
    'Every plan shows the right answers to every quiz.',
    welcome > 0 && `The ${welcome} min welcome call comes once, with the membership.`,
    plans.some((p) => p.callsPerWeek > 0) &&
      "Each week's calls are booked in that week. A call you don't use doesn't carry over.",
    plans.length > 1 && 'Moving up, you pay only the difference.',
    `Pause for up to ${PAUSE_WEEKS_OPTIONS.at(-1)} weeks.`,
    `A Platform fee of ${pct(PLATFORM_FEE)} (at least ${money(MIN_PLATFORM_FEE)}) is added at checkout.`,
  ].filter((r): r is string => Boolean(r));

  const ctaLabel = selected
    ? `Join with ${
        selected.callsPerWeek === 0 && welcome
          ? 'your first call'
          : planName(selected.callsPerWeek).toLowerCase()
      } for ${money(totalOf(selected))}`
    : '';

  const plansSection = selected && !subscription && (
    <section aria-labelledby="plans-title">
      <h2 id="plans-title" className={s.sech}>
        Join {first}
      </h2>
      <p className={s.lede}>Billed every 4 weeks. Cancel any time.</p>

      <div role="group" aria-labelledby="plans-title">
        {plans.map((plan) => {
          const on = plan.id === selected.id;
          const calls = plan.callsPerWeek * WEEKS;
          const sub =
            plan.callsPerWeek === 0
              ? `A ${plan.callMinutes} min welcome call with ${first}, and the right answers to every quiz`
              : `${calls} calls of ${plan.callMinutes} min, ${money(Math.round((plan.price / calls) * 100) / 100)} a call`;
          return (
            <button
              key={plan.id}
              type="button"
              aria-pressed={on}
              className={tw(s.tier, s.press, on && s.tierOn)}
              onClick={() => setPickedId(plan.id)}
            >
              <span className={s.radio} aria-hidden="true">
                <Check />
              </span>
              <span className={s.tierBody}>
                <span className={s.tname}>{planName(plan.callsPerWeek)}</span>
                <span className={s.tsub}>{sub}</span>
              </span>
              <span className={s.tprice}>
                <b>{money(plan.price)}</b>
                <span>per 4 weeks</span>
              </span>
            </button>
          );
        })}
      </div>

      <Strip plan={selected} firstName={first} />

      <button type="button" className={s.linkbtn} onClick={() => setSheet('billing')}>
        How billing works
      </button>

      <div className={s.dock}>
        <button
          type="button"
          disabled={isOwner}
          className={tw(s.cta, s.press)}
          onClick={() => setSheet('checkout')}
        >
          {isOwner ? 'This is what clients see' : ctaLabel}
        </button>
        <p className={s.fine}>
          {money(selected.price)} plus a {money(selected.platformFee)} Platform fee today, then
          every 4 weeks. Cancel any time.
        </p>
      </div>
    </section>
  );

  return (
    <div className={tw(fontClass, s.page, 'pt-beat')}>
      <div className={tw(s.layout, PAGE_WIDTH)}>
        <header className={s.hero}>
          <span
            className={s.blob}
            style={{ width: 260, height: 260, right: -90, top: -110 }}
            aria-hidden="true"
          />
          <span
            className={s.blob}
            style={{ width: 120, height: 120, right: 40, top: 120, opacity: 0.6 }}
            aria-hidden="true"
          />
          <div className={s.heroInner}>
            <h1 className={s.name}>
              {isOwner ? (
                <EditableText
                  value={name}
                  limit={NAME_MAX}
                  label="Name"
                  required="A page needs a name"
                  onSave={saveName}
                />
              ) : (
                name
              )}
            </h1>
            <div className={tw('sfv2', s.video)}>
              <IntroVideo
                name={name}
                playbackId={tutor.playbackId}
                picture={tutor.picture}
                priority
              />
            </div>
          </div>
        </header>

        <div className={s.side} ref={sideRef}>
          {subscription && (
            <section aria-labelledby="mine-title">
              <h2 id="mine-title" className={s.sech}>
                Your plan with {first}
              </h2>
              <div className={tw('sfv2', s.panel)}>
                <SubscriptionCard
                  subscription={subscription}
                  plans={plans}
                  tutorUsername={tutor.username}
                  tutorName={first}
                  serverNow={serverNow}
                />
              </div>
            </section>
          )}

          {/* What the client already owns stands before what is for sale. */}
          {earlier && (
            <section aria-labelledby="earlier-title">
              <h2 id="earlier-title" className={s.sech}>
                Your earlier calls with {first}
              </h2>
              <div className="sfv2 mt-3.5 flex flex-col gap-3">{earlier}</div>
            </section>
          )}

          {plansSection}
        </div>

        <div className={s.main}>
          {isOwner && (
            <section aria-labelledby="owner-title">
              <h2 id="owner-title" className={s.sech}>
                Your plans
              </h2>
              <div className={tw('sfv2', s.panel)}>
                <PlansEditor plans={plans} username={tutor.username} titled={false} />
              </div>
            </section>
          )}

          {(quizzes.length > 0 || isOwner) && (
            <section aria-labelledby="feed-title">
              <h2 id="feed-title" className={s.sech}>
                Latest from {first}
              </h2>
              <p className={s.lede}>
                Play any quiz. Sign in to see your score; members see the right answers.
              </p>
              {isOwner && (
                <QuizComposer username={tutor.username} firstName={first} picture={face} />
              )}
              {quizzes.map((quiz) => (
                <QuizPost
                  key={quiz.id}
                  quiz={quiz}
                  hidden={quiz.hidden ?? false}
                  tutor={{ username: tutor.username, firstName: first, picture: face }}
                  isOwner={isOwner}
                  loggedIn={loggedIn}
                  unlock={
                    !isOwner && cheapest
                      ? {
                          price: money(cheapest.price),
                          onUnlock: () => {
                            setPickedId(cheapest.id);
                            setSheet('checkout');
                          },
                        }
                      : null
                  }
                />
              ))}
            </section>
          )}
        </div>
      </div>

      {sheet === 'billing' && (
        <BottomSheet title="How billing works" onClose={() => setSheet(null)} close="Got it">
          <div>
            {rules.map((r) => (
              <div key={r} className={s.rule}>
                <span className={s.ico} aria-hidden="true">
                  <Check />
                </span>
                <span>{r}</span>
              </div>
            ))}
          </div>
        </BottomSheet>
      )}
      {/* Gone once the subscription is: the refresh after payment closes it. */}
      {sheet === 'checkout' && selected && !subscription && (
        <BottomSheet title={ctaLabel} onClose={() => setSheet(null)} close="Close">
          <Checkout plan={selected} tutorUsername={tutor.username} loggedIn={loggedIn} />
        </BottomSheet>
      )}
    </div>
  );
}

/** The sheet's money: the summary, the sign-in when needed, then Stripe's form in place. */
function Checkout({
  plan,
  tutorUsername,
  loggedIn,
}: {
  plan: SubscriptionPlanDto;
  tutorUsername: string;
  loggedIn: boolean;
}) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<'idle' | 'opening' | 'paying' | 'confirming'>('idle');
  const [checkout, setCheckout] = useState<{
    sessionId: string;
    clientSecret: string;
    publishableKey: string;
  } | null>(null);

  const open = useCallback(async () => {
    setStage('opening');
    setError(null);
    posthog.capture('subscription_checkout_started', {
      creator_username: tutorUsername,
      provider_username: tutorUsername,
      tutor_username: tutorUsername,
      calls_per_week: plan.callsPerWeek,
      price: plan.price,
    });
    const result = await callAction(() => startSubscription(tutorUsername, plan.id));
    if (!result.success) {
      setStage('idle');
      setError(result.error);
      return;
    }
    const { sessionId, url, clientSecret, publishableKey } = result.data;
    if (clientSecret && publishableKey) {
      setCheckout({ sessionId, clientSecret, publishableKey });
      setStage('paying');
      return;
    }
    if (url) {
      window.location.assign(url);
      return;
    }
    Sentry.captureMessage('Subscription checkout came back empty', { level: 'error' });
    setStage('idle');
    setError('Could not open the payment, try again');
  }, [plan, tutorUsername]);

  const confirm = useCallback(async () => {
    if (!checkout) return;
    setStage('confirming');
    for (let i = 0; i < CONFIRM_TRIES; i++) {
      const result = await callAction(() => confirmSubscription(checkout.sessionId, tutorUsername));
      if (result.success && result.data) {
        posthog.capture('subscription_checkout_completed', {
          creator_username: tutorUsername,
          provider_username: tutorUsername,
          tutor_username: tutorUsername,
          calls_per_week: plan.callsPerWeek,
        });
        break;
      }
      await new Promise((r) => setTimeout(r, CONFIRM_EVERY_MS));
    }
    // Confirmed, or paid and left for the webhook: either way the page reads it on refresh.
    router.refresh();
  }, [checkout, plan.callsPerWeek, router, tutorUsername]);

  return (
    <div>
      <dl className={s.sum}>
        <div>
          <dt>{planName(plan.callsPerWeek)}, 4 weeks</dt>
          <dd>{money(plan.price)}</dd>
        </div>
        {plan.callsPerWeek === 0 && (
          <div>
            <dt>Welcome call, {plan.callMinutes} min</dt>
            <dd>Included</dd>
          </div>
        )}
        <div>
          <dt>Right answers to every quiz</dt>
          <dd>Included</dd>
        </div>
        <div>
          <dt>Platform fee</dt>
          <dd>{money(plan.platformFee)}</dd>
        </div>
        <div className={s.sumTotal}>
          <dt>Due today</dt>
          <dd>{money(totalOf(plan))}</dd>
        </div>
      </dl>

      {!loggedIn ? (
        <div className="sfv2 mt-3.5">
          <InlineAuth
            id={`plan-${plan.id}`}
            source="plan_card"
            tutorUsername={tutorUsername}
            tone="card"
            prompt={`Sign in to join ${planName(plan.callsPerWeek).toLowerCase()}`}
            cta="Continue"
            onSuccess={open}
          />
        </div>
      ) : stage === 'confirming' ? (
        <p className={s.fine}>Paid, setting up your subscription</p>
      ) : checkout ? (
        <div className="mt-3.5 overflow-hidden rounded-[16px]">
          <EmbeddedCheckoutPanel
            clientSecret={checkout.clientSecret}
            publishableKey={checkout.publishableKey}
            onComplete={() => void confirm()}
          />
        </div>
      ) : (
        <button
          type="button"
          disabled={stage === 'opening'}
          className={tw(s.cta, s.press, 'mt-3.5')}
          onClick={() => void open()}
        >
          {stage === 'opening' ? 'Opening the payment…' : 'Continue to payment'}
        </button>
      )}
      {error && <p className={s.error}>{error}</p>}
    </div>
  );
}

/** The 4 weeks of the picked plan, drawn: the welcome call and the calls in each week. */
function Strip({ plan, firstName }: { plan: SubscriptionPlanDto; firstName: string }) {
  const membership = plan.callsPerWeek === 0;
  const title = membership
    ? `Your 4 weeks with ${firstName}`
    : `${plan.callsPerWeek * WEEKS} calls in your 4 weeks`;
  const sub = membership
    ? 'One welcome call to start.'
    : `${plan.callsPerWeek === 1 ? 'One call' : `${plan.callsPerWeek} calls`} a week, booked in that week.`;
  return (
    <div className={s.strip} aria-live="polite">
      <h3 className={s.stripTitle}>{title}</h3>
      <p className={s.stripSub}>{sub}</p>
      {/* Keyed by plan so the chips pop again on every pick. */}
      <div className={s.weeks} key={plan.id}>
        {Array.from({ length: WEEKS }, (_, w) => (
          <div key={w} className={s.wk}>
            <p>Week {w + 1}</p>
            {membership && w === 0 && (
              <div className={tw(s.chip, s.chipHello)}>Welcome call, {plan.callMinutes} min</div>
            )}
            {Array.from({ length: plan.callsPerWeek }, (_, d) => (
              <div key={d} className={tw(s.chip, s.chipCall)}>
                Call, {plan.callMinutes} min
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function BottomSheet({
  title,
  close,
  onClose,
  children,
}: {
  title: string;
  close: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Held in a ref so a parent re-render never re-runs the open effect and steals focus.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCloseRef.current();
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, []);

  return (
    <div className={s.scrim} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        ref={sheetRef}
        className={s.sheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.grab} aria-hidden="true" />
        <h2 id="sheet-title" className={s.sheetTitle}>
          {title}
        </h2>
        {children}
        <button
          ref={closeRef}
          type="button"
          className={tw(s.cta, s.ctaInk, s.press)}
          onClick={onClose}
        >
          {close}
        </button>
      </div>
    </div>
  );
}

const Check = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
